import React, { ReactElement, useEffect, useState } from 'react';
import { Space } from 'antd';
import { InputOTP } from 'antd-input-otp';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { authGetRecoveryFlow, authUpdateRecoveryFlow } from 'domains/auth/services/auth.service';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import {
  SCard,
  SWrapper,
  SInputWrapper,
} from './confirm-code.page.styles';

export const ConfirmCodePage = (): ReactElement => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [isConfirmCodeBtnLoading, setIsConfirmCodeBtnLoading] = useState<boolean>(false);
  const [isResendCodeBtnLoading, setIsResendCodeBtnLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!location.state?.flowId || !location.state?.email) {
      navigate(ROUTES_MAPPING.RESET_PASSWORD);
    }
  }, []);

  const getRecoveryFlowData = async (): Promise<{ email: string; csrfToken: string; flowId: string; }> => {
    try {
      const flowId = location.state.flowId || '';

      const flowData = await authGetRecoveryFlow({
        id: flowId,
      });

      let email = '';
      const emailData = flowData.ui.nodes.find(
        (node) => 'name' in node.attributes && node.attributes.name === 'email',
      );
      if ('value' in emailData!.attributes!) {
        email = emailData?.attributes?.value;
      }

      let csrfToken = '';
      const csrfTokenData = flowData.ui.nodes.find(
        (node) => 'name' in node.attributes && node.attributes.name === 'csrf_token',
      );
      if ('value' in csrfTokenData!.attributes!) {
        csrfToken = csrfTokenData?.attributes?.value;
      }

      return {
        email,
        csrfToken,
        flowId,
      };
    } catch (error) {
      console.error(error);

      return {
        email: '',
        csrfToken: '',
        flowId: '',
      };
    }
  };

  const onSubmitOtpCodeHandler = async (values: { otp: string[] }): Promise<void> => {
    try {
      setIsConfirmCodeBtnLoading(true);

      const { otp } = values;
      const recoveryFlowData = await getRecoveryFlowData();

      const updateRecoveryFlowData = await authUpdateRecoveryFlow({
        flow: recoveryFlowData?.flowId,
        updateRecoveryFlowBody: {
          csrf_token: recoveryFlowData?.csrfToken,
          code: otp.join(''),
          method: 'code',
        },
      });

      if (updateRecoveryFlowData?.ui?.messages?.[0]?.type === 'error') {
        form.setFields([
          {
            name: 'otp',
            errors: [updateRecoveryFlowData?.ui?.messages?.[0]?.text],
          },
        ]);

        setIsConfirmCodeBtnLoading(false);
      } else {
        navigate(ROUTES_MAPPING.PUBLIC.CREATE_NEW_PASSWORD, {
          state: {
            isOtpCodeApproved: true,
          }
        });
      }
    } catch (error) {
      console.error(error);
      setIsConfirmCodeBtnLoading(false);
    }
  };

  const onResendCodeHandler = async (): Promise<void> => {
    try {
      setIsResendCodeBtnLoading(true);

      const { csrfToken, flowId, email } = await getRecoveryFlowData();

      await authUpdateRecoveryFlow({
        flow: flowId,
        updateRecoveryFlowBody: {
          csrf_token: csrfToken,
          email,
          method: 'code',
        },
      });

      setIsResendCodeBtnLoading(false);
    } catch (error) {
      console.error(error);
      setIsResendCodeBtnLoading(false);
    }
  };

  return (
    <Space size={32} direction="vertical">
      <SWrapper>
        <b>{t('auth.confirmCode.text.title')}</b>
        <b>{location?.state?.email || ''}</b>
      </SWrapper>
      <SCard>
        <Form
          form={form}
          dataTestId="enter_code_form"
          onFinish={onSubmitOtpCodeHandler}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <SInputWrapper>
              <Form.Item
                name="otp"
                rules={[
                  {
                    message: 'Please fill the confirmation code',
                    validator: (_, value): Promise<any> => {
                      if (!value || value.includes(undefined) || value.includes('')) {
                        return Promise.reject();
                      }

                      return Promise.resolve();
                    }
                  }
                ]}
              >
                {/* @ts-ignore */}
                <InputOTP autoFocus inputType="numeric" />
              </Form.Item>
            </SInputWrapper>
            <Button
              loading={isConfirmCodeBtnLoading}
              type="primary"
              dataTestId="submit_button"
              htmlType="submit"
              size="large"
              block
            >
              <Trans i18nKey="auth.confirmCode.button.confirmCode" />
            </Button>
            <Button
              loading={isResendCodeBtnLoading}
              dataTestId="submit_button"
              size="large"
              onClick={onResendCodeHandler}
              block
            >
              <Trans i18nKey="auth.confirmCode.button.resend" />
            </Button>
          </Space>
        </Form>
      </SCard>
    </Space>
  );
};

export default ConfirmCodePage;
